import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, KeyRound, ArrowRight } from "lucide-react";
import { adminApi, setAdminToken, formatApiError, isAdminAuthenticated } from "../lib/adminAuth";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    isAdminAuthenticated().then((ok) => { if (ok) navigate("/admin/contracts", { replace: true }); });
  }, [navigate]);

  const requestCode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setInfo("");
    try {
      const { data } = await adminApi().post("/admin/login", { email });
      setInfo(data.message || "Code sent.");
      setStep("code");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await adminApi().post("/admin/verify", { email, code });
      setAdminToken(data.token);
      navigate("/admin/contracts", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-btacc-blue flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8" data-testid="admin-login-card">
        <Link to="/" className="block text-center mb-6">
          <h1 className="text-3xl font-montserrat font-bold text-btacc-blue">
            BT<span className="text-btacc-gold">ACC</span>
          </h1>
          <p className="text-xs text-btacc-secondary font-inter">Admin Access</p>
        </Link>

        <div className="flex items-center justify-center mb-6">
          <div className="bg-btacc-gold/15 p-3 rounded-full">
            <Lock size={22} className="text-btacc-blue" />
          </div>
        </div>

        {step === "email" && (
          <form onSubmit={requestCode} className="space-y-4" data-testid="admin-email-form">
            <div>
              <label className="block text-xs font-montserrat font-semibold text-btacc-blue mb-1.5 uppercase tracking-wide">
                Authorized Email
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-btacc-secondary" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md font-inter text-sm focus:outline-none focus:ring-2 focus:ring-btacc-gold focus:border-transparent"
                  placeholder="you@btacc.info"
                  data-testid="admin-email-input"
                  autoFocus
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-request-code-btn"
              className="w-full bg-btacc-blue text-white px-5 py-3 rounded-md font-montserrat font-bold text-sm hover:bg-btacc-secondary transition flex items-center justify-center disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send me a code"}
              <ArrowRight size={16} className="ml-2" />
            </button>
            <p className="text-xs font-inter text-btacc-secondary text-center pt-2">
              A 6-digit code will be sent to your email if it is authorized.
            </p>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={verifyCode} className="space-y-4" data-testid="admin-code-form">
            <div>
              <label className="block text-xs font-montserrat font-semibold text-btacc-blue mb-1.5 uppercase tracking-wide">
                6-Digit Code
              </label>
              <div className="relative">
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-btacc-secondary" />
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md font-inter text-lg tracking-[0.6em] font-mono focus:outline-none focus:ring-2 focus:ring-btacc-gold focus:border-transparent"
                  placeholder="••••••"
                  data-testid="admin-code-input"
                  autoFocus
                />
              </div>
              <p className="text-xs font-inter text-btacc-secondary mt-2">
                Sent to <strong>{email}</strong>. The code is valid for 10 minutes.
              </p>
            </div>
            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              data-testid="admin-verify-btn"
              className="w-full bg-btacc-gold text-btacc-blue px-5 py-3 rounded-md font-montserrat font-bold text-sm hover:bg-btacc-blue hover:text-white transition disabled:opacity-60"
            >
              {submitting ? "Verifying…" : "Sign in"}
            </button>
            <button
              type="button"
              onClick={() => { setStep("email"); setCode(""); setError(""); setInfo(""); }}
              className="w-full text-xs font-inter text-btacc-secondary hover:text-btacc-blue underline"
            >
              Use a different email
            </button>
          </form>
        )}

        {info && (
          <div data-testid="admin-info" className="mt-4 text-sm font-inter text-green-800 bg-green-50 border border-green-200 rounded-md p-3">
            {info}
          </div>
        )}
        {error && (
          <div data-testid="admin-error" className="mt-4 text-sm font-inter text-red-800 bg-red-50 border border-red-200 rounded-md p-3">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
